import { useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Select } from '@artosera/ui';
import {
  TYPES_MOUVEMENT,
  ETATS_MOUVEMENT,
  type TypeMouvement,
  type EtatMouvement,
} from '@artosera/shared';
import { useMouvements } from '../../api/mouvements.js';
import { useShellContext } from '../shell/context.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import { PastilleStatut } from '../../composants/PastilleStatut.js';
import { TON_ETAT } from './mouvementUi.js';
import { NouveauMouvementDialog } from './NouveauMouvementDialog.js';
import styles from './MouvementsPage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function MouvementsPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState<TypeMouvement | undefined>();
  const [etat, setEtat] = useState<EtatMouvement | undefined>();
  const [retard, setRetard] = useState(false);
  const [page, setPage] = useState(1);
  const liste = useMouvements(galerieSlug, {
    ...(type ? { type } : {}),
    ...(etat ? { etat: [etat] } : {}),
    enRetard: retard,
    page,
  });
  const date = (v: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(
      new Date(v),
    );
  return (
    <section className={styles.page} data-ui="page">
      <header className={styles.entete} data-ui="page-heading">
        <div>
          <h1>{t('regie:mouvements.titre')}</h1>
          <p>{t('regie:mouvements.sousTitre')}</p>
        </div>
        <Verrouillable
          action="movements:write"
          ouvert={galerieALaction(galerie, 'movements:write')}
        >
          <Button onClick={() => setParams({ nouveau: '1' })}>
            {t('regie:mouvements.nouveau')}
          </Button>
        </Verrouillable>
      </header>
      <div className={styles.filtres} data-ui="filters">
        <div className={styles.filtre}>
          <span className={styles.legende} aria-hidden="true">
            {t('regie:mouvements.filtres.type')}
          </span>
          <Select
            aria-label={t('regie:mouvements.filtres.type')}
            value={type ?? 'tous'}
            onValueChange={(v) => {
              setType(v === 'tous' ? undefined : (v as TypeMouvement));
              setPage(1);
            }}
            options={[
              { value: 'tous', label: t('regie:mouvements.filtres.tous') },
              ...TYPES_MOUVEMENT.map((v) => ({
                value: v,
                label: t(`regie:mouvements.types.${v}`),
              })),
            ]}
          />
        </div>
        <div className={styles.filtre}>
          <span className={styles.legende} aria-hidden="true">
            {t('regie:mouvements.filtres.etat')}
          </span>
          <Select
            aria-label={t('regie:mouvements.filtres.etat')}
            value={etat ?? 'tous'}
            onValueChange={(v) => {
              setEtat(v === 'tous' ? undefined : (v as EtatMouvement));
              setPage(1);
            }}
            options={[
              { value: 'tous', label: t('regie:mouvements.filtres.tous') },
              ...ETATS_MOUVEMENT.map((v) => ({
                value: v,
                label: t(`regie:mouvements.etats.${v}`),
              })),
            ]}
          />
        </div>
        <label className={styles.case}>
          <input
            type="checkbox"
            checked={retard}
            onChange={(e) => {
              setRetard(e.target.checked);
              setPage(1);
            }}
          />
          {t('regie:mouvements.filtres.enRetard')}
        </label>
      </div>
      {liste.isPending && (
        <div role="status" className={styles.squelette}>
          {t('commun:chargement')}
        </div>
      )}
      {liste.isError && (
        <div role="alert">
          <p>{t('commun:erreurReseau')}</p>
          <Button onClick={() => void liste.refetch()}>{t('regie:mouvements.reessayer')}</Button>
        </div>
      )}
      {liste.data?.mouvements.length === 0 && (
        <p>
          {t(type || etat || retard ? 'regie:mouvements.aucunResultat' : 'regie:mouvements.aucun')}
        </p>
      )}
      <div className={styles.liste} data-ui="object-list">
        {liste.data?.mouvements.map((m) => (
          <Link
            key={m.id}
            to={`/${galerieSlug}/mouvements/${m.id}`}
            className={`${styles.ligne} ${m.enRetard ? styles.ligneRetard : m.etat === 'en_cours' ? styles.ligneEnCours : ''}`}
            data-ui="object-row"
          >
            <div className={styles.oeuvres}>
              {m.lignes.slice(0, 3).map((l) => (
                <div className={styles.oeuvre} key={l.oeuvreId}>
                  <VignetteOeuvre url={l.apercuUrl ?? null} />
                  <ObjetCartel titre={l.titre} artiste={l.artisteNom} />
                  {l.statut && <PastilleStatut statut={l.statut} />}
                </div>
              ))}
              {m.lignes.length > 3 && (
                <span>{t('regie:mouvements.plusOeuvres', { count: m.lignes.length - 3 })}</span>
              )}
            </div>
            <div>
              <strong className={styles.type}>{t(`regie:mouvements.types.${m.type}`)}</strong>
              {m.contactNom && <p>{m.contactNom}</p>}
              <p>
                {date(m.departLe)}
                {m.retourPrevuLe && ` → ${date(m.retourPrevuLe)}`}
              </p>
              {m.bon && <span className={styles.numero}>{m.bon.numero}</span>}
            </div>
            <div>
              <Badge tone={TON_ETAT[m.etat]}>{t(`regie:mouvements.etats.${m.etat}`)}</Badge>
              {m.enRetard && (
                <p className={styles.retard}>
                  {t('regie:mouvements.retard', { count: m.joursDeRetard })}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
      {liste.data && liste.data.pagination.pages > 1 && (
        <nav aria-label={t('regie:mouvements.pagination')} className={styles.filtres}>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
            {t('regie:mouvements.precedent')}
          </Button>
          <span>
            {page} / {liste.data.pagination.pages}
          </span>
          <Button disabled={page >= liste.data.pagination.pages} onClick={() => setPage(page + 1)}>
            {t('regie:mouvements.suivant')}
          </Button>
        </nav>
      )}
      {galerieALaction(galerie, 'movements:write') && params.get('nouveau') === '1' && (
        <NouveauMouvementDialog
          registrePoliceActif={galerieAModule(galerie, 'registre-police')}
          galerieSlug={galerieSlug}
          ouvert
          onOuvertChange={(v) => {
            if (!v) setParams({});
          }}
          oeuvreInitialeId={params.get('oeuvre') ?? undefined}
        />
      )}
    </section>
  );
}
